import { useMyIdentityId } from 'queries/Auth.queries'
import { useTenantConfig } from 'features/tenants-endpoints-addresses/TenantsEndpointsAddresses.queries'
import { useMemo } from 'react'
import {
  EndpointAddressWithRelationships,
  Tenant_Endpoints_Addresses_Config,
} from 'features/tenants-endpoints-addresses/TenantsEndpointsAddresses.types'
import { Maybe, ResourceTypes } from '@vendorflow/types'
import {
  fetchEndpointAddressesUsingGET,
  fetchTenantConfigUsingGET,
} from 'features/tenants-endpoints-addresses/TenantsEndpointsAddresses.api'
import { MediumsEnum } from 'features/firebase/Firebase.constants'
import { SwitchboardEndpointsEnum } from 'features/switchboard/Switchboard.types'
import { DeprecatedMediumsToSwitchboardMediums } from 'features/tenants-endpoints/TenantsEndpoints.constants'

export function useDefaultEndpointAddressForBobForEndpoint(endpoint: Maybe<string>) {
  const bob = useMyIdentityId()
  const tenantConfig = useTenantConfig()

  return useMemo(() => {
    return getEndpointAddressForBobForEndpoint(tenantConfig.data, bob, endpoint)
  }, [bob, tenantConfig.dataUpdatedAt, endpoint])
}

export function getEndpointAddressForBobForEndpoint(
  config: Maybe<Tenant_Endpoints_Addresses_Config>,
  bob: Maybe<string>,
  endpoint: Maybe<string>
) {
  return config && bob && endpoint
    ? config.endpointAddresses.byRepresented[`${ResourceTypes.IDENTITIES}/${bob}`]?.[endpoint] || null
    : null
}

export async function generateTenantConfigBasedOnPolicy(tenant: string) {
  const config = await fetchTenantConfigUsingGET(tenant)
  const result: Tenant_Endpoints_Addresses_Config = {
    tenant: config,
    endpointAddresses: { byEndpoint: {}, byRepresented: {} },
  }

  return await addEndpointAddressesAndApplyMatchIdentityPolicy(result)
}

export async function addEndpointAddressesAndApplyMatchIdentityPolicy(config: Tenant_Endpoints_Addresses_Config) {
  const tenant = config.tenant.id
  const { endpointPreference } = config.tenant.relationships
  // these will be all the EndpointAddresses by their Endpoint ID
  const byEndpoint: Tenant_Endpoints_Addresses_Config['endpointAddresses']['byEndpoint'] = {}
  // this is a map of those represented by an EndpointAddress (Bobs or other entities) by Endpoint
  // i.e. compoundId -> endpoint.id -> endpointAddress.address
  // this allows us to know which entities (e.g. bobs or contact groups) have an assigned phone number (endpoint address) and for which endpoints (SMS, WhatsApp, etc.)
  const byRepresented: Tenant_Endpoints_Addresses_Config['endpointAddresses']['byRepresented'] = {}

  for (let index = 0; index < endpointPreference.length; index++) {
    const endpoint = endpointPreference[index].id
    let endpointAddresses: EndpointAddressWithRelationships[] = []
    try {
      endpointAddresses = await fetchEndpointAddressesUsingGET(tenant, endpoint)
    } catch (err) {
      console.error(`Error fetching endpoint addresses for tenant ${tenant} endpoint ${endpoint}`)
      console.error(err)
    }
    // store the EndpointAddresses in the byEndpoint map
    byEndpoint[endpointPreference[index].id] = endpointAddresses
    endpointAddresses.forEach((ea) => {
      // iterate through and store any entities represented by this EndpointAddress
      ea.relationships.represents.forEach((r) => {
        // compoundId e.g. `identities/abc123`
        const compoundId = `${r.type}/${r.id}`
        if (!byRepresented[compoundId]?.[endpoint]) {
          if (!byRepresented[compoundId]) {
            byRepresented[compoundId] = {}
          }
          byRepresented[compoundId][endpoint] = ea.address
        }
      })
    })
  }

  config.endpointAddresses = { byEndpoint, byRepresented }

  return config
}

export function convertToSwitchboardEndpoint(medium: string) {
  return DeprecatedMediumsToSwitchboardMediums[medium as MediumsEnum] || medium
}

// accounts for the deprecated MediumEnum
export function areEndpointsEqual(
  endpoint: SwitchboardEndpointsEnum,
  other: SwitchboardEndpointsEnum | MediumsEnum | null
) {
  return Boolean(
    other && (endpoint === other || endpoint === DeprecatedMediumsToSwitchboardMediums[other as MediumsEnum])
  )
}
